import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { referenceAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddExpenseTypeModal from '../components/AddExpenseTypeModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const sortByOrderThenName = (items = []) =>
  [...items].sort((a, b) => {
    if ((a.sort_order || 0) !== (b.sort_order || 0)) {
      return (a.sort_order || 0) - (b.sort_order || 0);
    }
    return (a.name || '').localeCompare(b.name || '');
  });

const AdminExpenseTypesPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    name: '',
    prorate_business_use: false,
    active: true,
  });

  const loadData = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const [typesRes, categoriesRes] = await Promise.all([
        referenceAPI.getExpenseTypes(),
        referenceAPI.getExpenseTypeCategories(),
      ]);

      const sortedCategories = sortByOrderThenName(toArray(categoriesRes.data, 'expense type categories'));
      const sortedTypes = [...toArray(typesRes.data, 'expense types')].sort((a, b) => {
        if ((a.category || '').localeCompare(b.category || '') !== 0) {
          return (a.category || '').localeCompare(b.category || '');
        }
        if ((a.sort_order || 0) !== (b.sort_order || 0)) {
          return (a.sort_order || 0) - (b.sort_order || 0);
        }
        return (a.name || '').localeCompare(b.name || '');
      });

      setCategories(sortedCategories);
      setTypes(sortedTypes);
    } catch (err) {
      console.error('Error loading expense types:', err);
      setError(err.response?.data?.error || 'Failed to load expense types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEdit = (type) => {
    fields.reset();
    setSelectedType(type);
    setFormData({
      category_id: type.category_id ? String(type.category_id) : '',
      name: type.name || '',
      prorate_business_use: Boolean(type.prorate_business_use),
      active: Boolean(type.active),
    });
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedType(null);
    setFormData({
      category_id: '',
      name: '',
      prorate_business_use: false,
      active: true,
    });
    setEditDialogOpen(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async () => {
    if (!selectedType) return;
    const valid = fields.validate({
      category_id: !formData.category_id && 'Category is required',
      name: !formData.name.trim() && 'Name is required',
    });
    if (!valid) return;

    setSaving(true);
    try {
      await referenceAPI.updateExpenseType(selectedType.id, {
        category_id: Number(formData.category_id),
        name: formData.name.trim(),
        prorate_business_use: Boolean(formData.prorate_business_use),
        active: Boolean(formData.active),
      });
      handleCloseEdit();
      loadData();
    } catch (err) {
      console.error('Error updating expense type:', err);
      notify.error(errorMessage(err, 'Failed to update expense type'));
    } finally {
      setSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (type) => setDeleteTarget(type);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await referenceAPI.deleteExpenseType(deleteTarget.id);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      console.error('Error deleting expense type:', err);
      notify.error(errorMessage(err, 'Failed to deactivate expense type'));
    }
  };

  const handleToggleActive = async (type) => {
    try {
      await referenceAPI.updateExpenseType(type.id, {
        active: !type.active,
      });
      loadData();
    } catch (err) {
      console.error('Error toggling active state:', err);
      notify.error(errorMessage(err, 'Failed to update status'));
    }
  };

  const categoryName = (categoryId) =>
    categories.find((cat) => cat.id === categoryId)?.name || 'Unassigned';

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Expense Types</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Expense Type
            </Box>
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
      <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="center">Home-Office %</TableCell>
              <TableCell align="center">Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={5} />}
            {types.map((type) => (
              <TableRow key={type.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(type) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(type), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{categoryName(type.category_id)}</TableCell>
                <TableCell>{type.name}</TableCell>
                <TableCell align="center">
                  {type.prorate_business_use && (
                    <Chip label="Prorated" size="small" color="info" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Switch
                      checked={Boolean(type.active)}
                      onChange={() => handleToggleActive(type)}
                      color="primary"
                    />
                    {!type.active && (
                      <Chip label="Inactive" size="small" color="warning" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && types.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No expense types found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <AddExpenseTypeModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        apiCall={referenceAPI.createExpenseType}
        onSuccess={() => {
          setOpenAddModal(false);
          loadData();
        }}
      />

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expense Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              disableClearable
              options={categories}
              getOptionLabel={(category) => category.name}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              filterOptions={createPrefixFilter((category) => category.name)}
              value={categories.find((category) => String(category.id) === formData.category_id) ?? null}
              onChange={(e, newValue) => {
                fields.clear('category_id');
                setFormData((prev) => ({ ...prev, category_id: newValue ? String(newValue.id) : '' }));
              }}
              renderInput={(params) => <TextField {...params} label="Category" required {...fields.props('category_id')} />}
            />
            <TextField
              size="small"
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleEditChange}
              {...fields.props('name')}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.prorate_business_use)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      prorate_business_use: e.target.checked,
                    }))
                  }
                />
              }
              label="Prorate by home-office %"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5, ml: 6 }}>
              For shared household costs (cell, internet, house electric). Only the
              home-office percentage set on the Business Info page is counted as
              deductible in the tax report.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.active)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate Expense Type"
        message={`Deactivate expense type "${deleteTarget?.name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLabel="Deactivate"
        confirmColor="warning"
      />
    </Box>
  );
};

export default AdminExpenseTypesPage;

